import type { EncounterRecord } from '../../types';

interface LoadedPhoto {
  source: CanvasImageSource;
  width: number;
  height: number;
  release: () => void;
}

async function loadPhoto(blob: Blob): Promise<LoadedPhoto> {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(blob);
      return { source: bitmap, width: bitmap.width, height: bitmap.height, release: () => bitmap.close() };
    } catch {
      // Safari and a few embedded browsers may reject createImageBitmap for an otherwise valid image.
    }
  }

  const url = URL.createObjectURL(blob);
  const image = new Image();
  image.decoding = 'async';
  image.src = url;
  try {
    await image.decode();
    return { source: image, width: image.naturalWidth, height: image.naturalHeight, release: () => URL.revokeObjectURL(url) };
  } catch (error) {
    URL.revokeObjectURL(url);
    throw error;
  }
}

function drawCornerMarks(context: CanvasRenderingContext2D, x: number, y: number, width: number, height: number) {
  const inset = 18;
  const arm = 36;
  context.save();
  context.strokeStyle = 'rgba(231, 231, 227, 0.82)';
  context.lineWidth = 2;
  const corners = [
    [x + inset, y + inset, 1, 1],
    [x + width - inset, y + inset, -1, 1],
    [x + inset, y + height - inset, 1, -1],
    [x + width - inset, y + height - inset, -1, -1],
  ] as const;
  for (const [cornerX, cornerY, xDirection, yDirection] of corners) {
    context.beginPath();
    context.moveTo(cornerX + arm * xDirection, cornerY);
    context.lineTo(cornerX, cornerY);
    context.lineTo(cornerX, cornerY + arm * yDirection);
    context.stroke();
  }
  context.restore();
}

function drawPeopleLabel(context: CanvasRenderingContext2D, label: string, x: number, baseline: number) {
  context.save();
  context.font = '600 26px "Cascadia Mono", Consolas, monospace';
  const horizontalPadding = 24;
  const markerWidth = 9;
  const markerGap = 15;
  const boxWidth = Math.min(760, Math.ceil(context.measureText(label).width) + horizontalPadding * 2 + markerWidth + markerGap);
  const boxHeight = 62;
  const boxY = baseline - 43;
  context.fillStyle = 'rgba(8, 9, 9, 0.82)';
  context.fillRect(x, boxY, boxWidth, boxHeight);
  context.strokeStyle = 'rgba(231, 231, 227, 0.62)';
  context.lineWidth = 2;
  context.strokeRect(x, boxY, boxWidth, boxHeight);
  context.fillStyle = '#e85a1c';
  context.fillRect(x + horizontalPadding, boxY + 26, markerWidth, markerWidth);
  context.fillStyle = '#e7e7e3';
  context.fillText(label, x + horizontalPadding + markerWidth + markerGap, baseline);
  context.restore();
}

function canvasToPng(canvas: HTMLCanvasElement) {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('圖片輸出失敗')), 'image/png');
  });
}

export async function composeResultImage(encounter: EncounterRecord): Promise<Blob> {
  const photo = await loadPhoto(encounter.photoBlob);
  try {
    if (!photo.width || !photo.height) throw new Error('拍攝影像尺寸無效');

    const canvas = document.createElement('canvas');
    const photoFrame = {
      x: 74,
      y: 208,
      width: 932,
      height: Math.round(932 * photo.height / photo.width),
    };
    const photoBottom = photoFrame.y + photoFrame.height;
    const metadataTop = photoBottom + 72;
    const signatureTop = metadataTop + 222;
    const signatureHeight = 282;
    canvas.width = 1080;
    canvas.height = signatureTop + signatureHeight + 78;

    const context = canvas.getContext('2d');
    if (!context) throw new Error('無法建立輸出畫布');

    context.fillStyle = '#080909';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.strokeStyle = '#343636';
    context.lineWidth = 2;
    context.strokeRect(46, 46, 988, canvas.height - 92);

    context.fillStyle = '#e7e7e3';
    context.font = '700 42px Arial, sans-serif';
    context.fillText('G-CAMERA / 相遇結果', 74, 112);
    context.fillStyle = '#e85a1c';
    context.font = '500 36px "Cascadia Mono", Consolas, monospace';
    context.fillText(encounter.id, 74, 168);

    // Use the blob's exact aspect ratio so the already-captured composition is never cropped again.
    context.drawImage(photo.source, photoFrame.x, photoFrame.y, photoFrame.width, photoFrame.height);
    context.strokeStyle = '#565854';
    context.strokeRect(photoFrame.x, photoFrame.y, photoFrame.width, photoFrame.height);
    drawCornerMarks(context, photoFrame.x, photoFrame.y, photoFrame.width, photoFrame.height);
    drawPeopleLabel(
      context,
      encounter.people.map((person) => person.label).join(' × ') || 'GUEST',
      photoFrame.x + 24,
      photoBottom - 42,
    );

    const date = new Date(encounter.createdAt);
    const gps = encounter.location.latitude == null
      ? 'GPS 未取得'
      : `${encounter.location.latitude.toFixed(4)}°, ${encounter.location.longitude?.toFixed(4)}°`;
    context.fillStyle = '#e7e7e3';
    context.font = '500 25px "Cascadia Mono", Consolas, monospace';
    context.fillText(`${date.toLocaleDateString('zh-TW')}  ${date.toLocaleTimeString('zh-TW', { hour12: false })}`, 74, metadataTop);
    context.fillStyle = '#858681';
    context.fillText(gps, 74, metadataTop + 42);

    encounter.palette.slice(0, 5).forEach((sample, index) => {
      context.fillStyle = sample.hex;
      context.fillRect(74 + index * 115, metadataTop + 72, 100, 48);
    });
    context.fillStyle = '#e85a1c';
    context.font = '24px "Cascadia Mono", Consolas, monospace';
    context.fillText(encounter.paletteName ?? 'STEEL SHADOW', 74, metadataTop + 154);
    context.fillStyle = '#858681';
    context.textAlign = 'right';
    context.fillText('ONE-OFF / LOCAL ARCHIVE', 1006, metadataTop + 154);
    context.textAlign = 'left';

    context.strokeStyle = '#343636';
    context.strokeRect(74, signatureTop, 932, signatureHeight);
    context.beginPath();
    context.moveTo(74, signatureTop + 52);
    context.lineTo(1006, signatureTop + 52);
    context.stroke();
    context.fillStyle = '#e7e7e3';
    context.font = '500 22px "Cascadia Mono", Consolas, monospace';
    context.fillText('SIGNATURE / 簽名', 94, signatureTop + 34);
    context.fillStyle = '#858681';
    context.textAlign = 'right';
    context.fillText('MULTI-STROKE · ONE-OFF', 986, signatureTop + 34);
    context.textAlign = 'left';

    if (encounter.signature?.type === 'svg-path' && typeof encounter.signature.data === 'string') {
      context.save();
      context.translate(94, signatureTop + 62);
      context.scale(1.08, 0.84);
      context.strokeStyle = '#e7e7e3';
      context.lineWidth = 4;
      context.lineCap = 'round';
      context.lineJoin = 'round';
      context.stroke(new Path2D(encounter.signature.data));
      context.restore();
    }

    context.fillStyle = '#565854';
    context.font = '18px "Cascadia Mono", Consolas, monospace';
    context.fillText('G-CAMERA / ORIGINAL CAPTURE COMPOSITION', 74, canvas.height - 32);

    return await canvasToPng(canvas);
  } finally {
    photo.release();
  }
}

export async function shareOrDownload(blob: Blob, filename: string) {
  const file = new File([blob], filename, { type: 'image/png' });
  if (navigator.canShare?.({ files: [file] })) {
    await navigator.share({ files: [file], title: 'G-Camera 相遇作品' });
    return 'shared' as const;
  }
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 30000);
  return 'downloaded' as const;
}
